import type { ContextMenuEntry } from "@/components/common/ContextMenu";
import type { FileTreeEntry } from "@/components/file-tree/useFileTree";
import { extractEnvironmentArchive } from "@/lib/environment-files";
import { isArchiveFileName } from "@/lib/file-type";

export interface FileTypeActionContext {
	entry: FileTreeEntry;
	environmentId: string | null;
	/** 展開で生成されたフォルダを開くなど、ツリー側の後処理。 */
	onToggleExpand: (path: string, expanded: boolean) => void;
}

/** 特定のファイル種別にだけ出す右クリックメニュー項目を提供するハンドラー。 */
export interface FileTypeActionHandler {
	/** このハンドラーが扱うファイル名か (拡張子で判定)。 */
	matches: (fileName: string) => boolean;
	buildActions: (context: FileTypeActionContext) => ContextMenuEntry[];
}

const archiveHandler: FileTypeActionHandler = {
	matches: isArchiveFileName,
	buildActions: ({ entry, environmentId, onToggleExpand }) => [
		{
			label: "展開",
			testId: "file-node-menu-extract",
			disabled: environmentId === null,
			onSelect: () => {
				if (environmentId === null) return;
				void (async () => {
					try {
						const result = await extractEnvironmentArchive(environmentId, entry.path);
						if (result.isDirectory) onToggleExpand(result.destinationPath, true);
					} catch (err) {
						// extractEnvironmentArchive 内で失敗トースト表示済み。ログに残したうえで再送出する。
						console.error("アーカイブの展開に失敗しました。", err);
						throw err;
					}
				})();
			},
		},
	],
};

/** ファイル種別 → 追加メニュー項目のレジストリ。新しい種別を扱うときはここへハンドラーを追加する。 */
const FILE_TYPE_ACTION_HANDLERS: readonly FileTypeActionHandler[] = [archiveHandler];

/** ファイル名に合致するハンドラーのメニュー項目を登録順に連結して返す。合致がなければ空配列。 */
export function buildFileTypeActions(context: FileTypeActionContext): ContextMenuEntry[] {
	return FILE_TYPE_ACTION_HANDLERS.filter((handler) => handler.matches(context.entry.name)).flatMap(
		(handler) => handler.buildActions(context),
	);
}
